import { clsx, type ClassValue } from "clsx";
import { extendTailwindMerge } from "tailwind-merge";

/*
 * Wise Owl `cn()` — class composition with brand-aware conflict
 * resolution.
 *
 * Why this is non-trivial: tailwind-merge classifies `text-*`,
 * `bg-*`, `border-*` etc. into groups so conflicts inside a group
 * collapse to the last-declared. Out of the box it knows Tailwind's
 * standard color and size names — it does NOT know our custom token
 * names (`teal`, `cream`, `ink`, `m-stressed`, `display`, `eyebrow`,
 * etc.).
 *
 * Without this configuration, `cn("text-cream", "text-body")` would
 * be treated as two unknown text-* classes and collapsed to the last
 * one — meaning every <Button> in the app ends up inheriting the
 * body's ink color because the size class (`text-body`) silently
 * wins over the variant color (`text-cream`). That's the unreadable
 * teal-on-teal button the screenshots show.
 *
 * Telling tailwind-merge which custom tokens are font-sizes and
 * which are colors fixes the conflict properly, so `text-cream` and
 * `text-body` can coexist without one eating the other.
 */

// Every color exposed through the tokens preset (must stay in sync
// with packages/tokens/src/tailwind-preset.cjs).
const BRAND_COLORS = [
  "teal", "teal-2", "teal-deep",
  "mustard", "mustard-2", "mustard-soft",
  "cream", "cream-2", "paper",
  "ink", "ink-2", "ink-soft",
  "muted", "muted-2",
  "line", "line-strong",
  "success", "warning", "danger", "info",
  "m-calm", "m-stressed", "m-confused", "m-lonely",
  "m-tired", "m-happy", "m-overthinking", "m-unmotivated",
] as const;

const BRAND_FONT_SIZES = [
  "display", "h1", "h2", "h3", "body", "small", "meta", "eyebrow",
] as const;

const customMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      "font-size":    [{ text: [...BRAND_FONT_SIZES] }],
      "text-color":   [{ text: [...BRAND_COLORS] }],
      "bg-color":     [{ bg: [...BRAND_COLORS] }],
      "border-color": [{ border: [...BRAND_COLORS] }],
      "ring-color":   [{ ring: [...BRAND_COLORS] }],
      "ring-offset-color": [{ "ring-offset": [...BRAND_COLORS] }],
      // Custom radius alias `pill` (= 9999px) — distinct group from
      // numeric radii so `rounded-pill` is preserved next to `rounded-md`.
      rounded:  [{ rounded: ["pill"] }],
      // Custom shadow names from the brand spec.
      shadow:   [{ shadow: ["1", "2", "glow"] }],
    },
  },
});

export const cn = (...inputs: ClassValue[]): string => customMerge(clsx(inputs));
